import React from 'react'
import { CreditApplication, PersonaInquiry } from '_gql'
import { css } from '@emotion/core'

import { useMakeTestId } from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'

import ImageWrapper from '@sputnik/components/ImageWrapper'
import useGoToOffers from '@sputnik/hooks/useGoToOffers'
import PersonVerificationButtons from '@sputnik/pages/Dashboard/Status/PersonVerificationButtons'
import StatusTile from '@sputnik/pages/Dashboard/StatusTile/StatusTile'

interface MoreInfoNeededStatusPersonaTileProps {
  creditApp: CreditApplication
  personaInquiriesToComplete: PersonaInquiry[]
  refetch: () => void
}

const MoreInfoNeededStatusPersonaTile = ({
  creditApp,
  personaInquiriesToComplete = [],
  refetch,
}: MoreInfoNeededStatusPersonaTileProps) => {
  const makeTestId = useMakeTestId('MoreInfoNeededStatusPersonaTile')
  const { salesforceCreditId, lastUpdatedByApplicantAt, authorizedSigner } = creditApp || {}

  const { decisions, handleGoToOffers, isLoggedInUserAuthorizedSigner } = useGoToOffers({
    salesforceCreditId,
    authSignerEmail: authorizedSigner?.email as string,
  })
  const hasSelectedDecision = !!decisions?.find((decision) => decision.userSelectedAt)
  const showGoToOfferButton = isLoggedInUserAuthorizedSigner && decisions?.length > 0 && !hasSelectedDecision

  return (
    <div
      css={tilesHolderCss}
      data-testid={makeTestId('')}
    >
      {personaInquiriesToComplete.map((personaInq, index) => {
        return (
          <div
            key={index}
            data-testid={makeTestId('persona-inquiries')}
          >
            <StatusTile
              key={'persona' + index}
              creditApp={creditApp}
              image={
                <ImageWrapper
                  alt='persona image'
                  imageName={`dashboard-application-persona-${index + 1}`}
                />
              }
              primaryInfo={
                <FormattedMessage
                  key={personaInq?.inquiryId}
                  defaultMessage='Verification is needed for {name}. {showGoToOfferButton, select, 
                      true {In the meantime, you can review and sign your offer.}
                      other {}
                    }'
                  id='sputnik.MoreInfoNeededStatusPersonaTile__IBjbXB'
                  values={{
                    name: <b>{`${personaInq?.person?.firstName} ${personaInq?.person?.lastName}`}</b>,
                    showGoToOfferButton,
                  }}
                />
              }
              secondaryInfo={
                <FormattedMessage
                  defaultMessage='Last updated on {date, date, long}'
                  id='sputnik.MoreInfoNeededStatusPersonaTile__nqBLM8'
                  values={{ date: new Date(lastUpdatedByApplicantAt) }}
                />
              }
              title={
                <FormattedMessage
                  defaultMessage='Personal identification'
                  id='sputnik.MoreInfoNeededStatusPersonaTile__aGOcUr'
                />
              }
            >
              <PersonVerificationButtons
                key={personaInq?.inquiryId}
                decisions={decisions}
                handleGoToOffers={handleGoToOffers}
                inquiry={personaInq}
                refetch={refetch}
                salesforceCreditId={salesforceCreditId}
                showGoToOfferButton={showGoToOfferButton}
              />
            </StatusTile>
          </div>
        )
      })}
    </div>
  )
}

const tilesHolderCss = css`
  display: flex;
  flex-direction: column;
  gap: var(--tri-space-500);

  .verify-persona-button {
    margin-top: var(--tri-space-200);
  }
`

export default MoreInfoNeededStatusPersonaTile
